import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { testCaseAPI, testResultAPI, proxyAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function EndpointEvolution() {
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCase, setSelectedTestCase] = useState('');
  const [evolutionData, setEvolutionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proxies, setProxies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters (only compare main-stage results)
  const [filters, setFilters] = useState({
    stage: 'main',
    dateFrom: '',
    dateTo: '',
    limit: 50,
    proxyId: ''
  });

  // Comparison
  const [selectedResults, setSelectedResults] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    fetchTestCases();
    fetchProxies();
  }, []);

  useEffect(() => {
    if (selectedTestCase) {
      fetchEvolution();
    }
  }, [selectedTestCase, filters]);

  const fetchTestCases = async () => {
    try {
      const response = await testCaseAPI.getAll();
      setTestCases(response.data);
    } catch (err) {
      console.error('Failed to fetch test cases:', err);
    }
  };

  const fetchProxies = async () => {
    try {
      const response = await proxyAPI.getAll();
      setProxies(response.data);
    } catch (err) {
      console.error('Failed to fetch proxies:', err);
    }
  };

  const fetchEvolution = async () => {
    try {
      setLoading(true);
      const response = await testResultAPI.getEvolution(selectedTestCase, filters);
      setEvolutionData(response.data);
      setSelectedResults([]);
      setComparisonData(null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCompare = async () => {
    if (selectedResults.length < 2) {
      alert('Please select at least 2 results to compare');
      return;
    }

    try {
      setComparing(true);
      const response = await testResultAPI.compare(selectedResults);
      setComparisonData(response.data);
    } catch (err) {
      alert('Failed to compare results: ' + err.message);
    } finally {
      setComparing(false);
    }
  };

  const toggleResultSelection = (resultId) => {
    setSelectedResults(prev => {
      if (prev.includes(resultId)) {
        return prev.filter(id => id !== resultId);
      } else {
        return [...prev, resultId];
      }
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const selectedTestCaseObj = testCases.find(tc => tc._id === selectedTestCase);

  return (
    <div>
      <div className="card">
        <h2>Endpoint Evolution Tracking</h2>
        <p className="text-muted">
          Track how endpoint performance evolves over time. Only Main-stage results are shown for reliable comparison.
        </p>

        {/* Test Case Selection */}
        <div className="grid grid-2 mb-3">
          <div className="form-group">
            <label>Select Test Case</label>
            <select
              value={selectedTestCase}
              onChange={(e) => setSelectedTestCase(e.target.value)}
            >
              <option value="">-- Select a test case --</option>
              {testCases.map(tc => (
                <option key={tc._id} value={tc._id}>
                  {tc.name} ({tc.project})
                </option>
              ))}
            </select>
          </div>

          {selectedTestCase && (
            <div className="form-group">
              <label>Test Case Info</label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <strong>{selectedTestCaseObj?.endpoint}</strong>
                <br />
                <small className="text-muted">Method: {selectedTestCaseObj?.method}</small>
              </div>
            </div>
          )}
        </div>

        {selectedTestCase && (
          <>
            {/* Filters Toggle */}
            <div className="flex-between mb-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn btn-sm btn-secondary"
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>Filters</h3>
              <div className="grid grid-4">
                <div className="form-group">
                  <label>Date From</label>
                  <input
                    type="datetime-local"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Date To</label>
                  <input
                    type="datetime-local"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Proxy</label>
                  <select
                    value={filters.proxyId}
                    onChange={(e) => handleFilterChange('proxyId', e.target.value)}
                  >
                    <option value="">All Proxies</option>
                    <option value="none">No Proxy</option>
                    {proxies.map(proxy => (
                      <option key={proxy._id} value={proxy._id}>{proxy.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Limit</label>
                  <select
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  >
                    <option value="20">Last 20</option>
                    <option value="50">Last 50</option>
                    <option value="100">Last 100</option>
                    <option value="200">Last 200</option>
                  </select>
                </div>
              </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
              </div>
            ) : evolutionData && evolutionData.timeSeries.length > 0 ? (
              <>
                {/* Charts */}
                <div className="mb-4">
                  <h3>Success Rate Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={evolutionData.timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={formatTimestamp}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis domain={[0, 100]} label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip labelFormatter={formatTimestamp} />
                      <Legend />
                      <Line type="monotone" dataKey="successRate" stroke="#28a745" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mb-4">
                  <h3>Latency Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={evolutionData.timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={formatTimestamp}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis label={{ value: 'Latency (ms)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip labelFormatter={formatTimestamp} />
                      <Legend />
                      <Line type="monotone" dataKey="avgLatency" stroke="#007bff" strokeWidth={2} name="Avg Latency" />
                      <Line type="monotone" dataKey="p95Latency" stroke="#ffc107" strokeWidth={2} name="P95 Latency" />
                      <Line type="monotone" dataKey="p99Latency" stroke="#dc3545" strokeWidth={2} name="P99 Latency" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Results Table with Selection */}
                <div className="mt-4">
                  <div className="flex-between mb-3">
                    <h3>Test Results</h3>
                    <button
                      onClick={handleCompare}
                      className="btn btn-primary"
                      disabled={selectedResults.length < 2 || comparing}
                    >
                      {comparing ? 'Comparing...' : `Compare Selected (${selectedResults.length})`}
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedResults(evolutionData.timeSeries.map(r => r.resultId));
                                } else {
                                  setSelectedResults([]);
                                }
                              }}
                              checked={selectedResults.length === evolutionData.timeSeries.length}
                            />
                          </th>
                          <th>Timestamp</th>
                          <th>Success Rate</th>
                          <th>Avg Latency</th>
                          <th>Requests</th>
                          <th>Proxy</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolutionData.timeSeries.map((result) => (
                          <tr key={result.resultId}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedResults.includes(result.resultId)}
                                onChange={() => toggleResultSelection(result.resultId)}
                              />
                            </td>
                            <td>{new Date(result.timestamp).toLocaleString()}</td>
                            <td>
                              <span className={`badge ${result.successRate >= 95 ? 'badge-success' : 'badge-warning'}`}>
                                {result.successRate.toFixed(1)}%
                              </span>
                            </td>
                            <td>{result.avgLatency.toFixed(2)}ms</td>
                            <td>{result.totalRequests}</td>
                            <td>{result.proxyName}</td>
                            <td>
                              <Link
                                to={`/result/${result.resultId}`}
                                className="btn btn-sm btn-secondary"
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Comparison Table */}
                {comparisonData && (
                  <div className="mt-4">
                    <h3>Comparison Results</h3>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Success Rate</th>
                            <th>Avg Latency</th>
                            <th>P95 Latency</th>
                            <th>P99 Latency</th>
                            <th>Total Requests</th>
                            <th>Proxy</th>
                            <th>Concurrency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.comparison.map((comp, index) => (
                            <tr key={comp.id}>
                              <td>{new Date(comp.timestamp).toLocaleString()}</td>
                              <td>
                                <span className={`badge ${comp.metrics.successRate >= 95 ? 'badge-success' : 'badge-warning'}`}>
                                  {comp.metrics.successRate.toFixed(1)}%
                                </span>
                              </td>
                              <td>{comp.metrics.avgLatency.toFixed(2)}ms</td>
                              <td>{comp.metrics.p95Latency.toFixed(2)}ms</td>
                              <td>{comp.metrics.p99Latency.toFixed(2)}ms</td>
                              <td>{comp.metrics.totalRequests}</td>
                              <td>{comp.proxyName}</td>
                              <td>{comp.concurrency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Delta Analysis */}
                    {comparisonData.comparison.length === 2 && (
                      <div className="card mt-3" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                        <h4>Delta Analysis (Change from first to last)</h4>
                        <div className="grid grid-4">
                          {(() => {
                            const first = comparisonData.comparison[0].metrics;
                            const last = comparisonData.comparison[1].metrics;
                            const deltaSuccessRate = last.successRate - first.successRate;
                            const deltaLatency = last.avgLatency - first.avgLatency;
                            const deltaP95 = last.p95Latency - first.p95Latency;
                            const deltaP99 = last.p99Latency - first.p99Latency;

                            return (
                              <>
                                <div>
                                  <strong>Success Rate:</strong>{' '}
                                  <span className={deltaSuccessRate >= 0 ? 'badge badge-success' : 'badge-danger'}>
                                    {deltaSuccessRate >= 0 ? '+' : ''}{deltaSuccessRate.toFixed(2)}%
                                  </span>
                                </div>
                                <div>
                                  <strong>Avg Latency:</strong>{' '}
                                  <span className={deltaLatency <= 0 ? 'badge badge-success' : 'badge-danger'}>
                                    {deltaLatency >= 0 ? '+' : ''}{deltaLatency.toFixed(2)}ms
                                  </span>
                                </div>
                                <div>
                                  <strong>P95 Latency:</strong>{' '}
                                  <span className={deltaP95 <= 0 ? 'badge badge-success' : 'badge-danger'}>
                                    {deltaP95 >= 0 ? '+' : ''}{deltaP95.toFixed(2)}ms
                                  </span>
                                </div>
                                <div>
                                  <strong>P99 Latency:</strong>{' '}
                                  <span className={deltaP99 <= 0 ? 'badge badge-success' : 'badge-danger'}>
                                    {deltaP99 >= 0 ? '+' : ''}{deltaP99.toFixed(2)}ms
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="alert alert-info">
                No evolution data available for this test case. Run some tests to see evolution over time.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default EndpointEvolution;
